import React, { useState, useEffect } from 'react';
import { 
  Eye, 
  CheckCircle, 
  XCircle, 
  Search, 
  Filter,
  RefreshCw,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Clock,
  User,
  AlertCircle,
  CheckSquare,
  XSquare,
  MessageSquare,
  Download,
  Activity,
  BarChart3,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  Filter as FilterIcon,
  CalendarDays,
  CreditCard,
  Wallet,
  Sparkles,
  Zap,
  Target,
  Award,
  Lock
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import financialCaseApi from '../../services/accountant/financialCaseApi';
import { formatCurrency } from '../../utils/currencyUtils';


const AccountantFinance = () => {
  const { token, user } = useAuth();
  const [financialCases, setFinancialCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [currencyFilter, setCurrencyFilter] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' hoặc 'reject'
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statistics, setStatistics] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Helper function: Check if current user can approve/reject a case
  const canApproveRejectCase = (financialCase) => {
    // Admin luôn có quyền duyệt
    if (user?.role === 'admin') {
      return true;
    }
    
    // Accountant chỉ duyệt được nếu:
    // 1. Case chưa assign cho ai (assigned_accountant_id = null)
    // 2. Hoặc case được assign cho chính mình
    if (user?.role === 'accountant') {
      return !financialCase.assigned_accountant_id || 
             financialCase.assigned_accountant_id === user.id;
    }
    
    return false;
  };

  // Clear cache và reload financial cases
  const refreshWithClearCache = async () => {
    console.log('🔄 [REFRESH] Clearing cache and reloading data...');
    
    // Show loading toast
    const toastId = toast.loading('🔄 Đang xóa cache và tải lại dữ liệu...');
    
    try {
      // Clear browser cache for API calls
      if ('caches' in window) {
        try {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => {
              console.log('🗑️ [CACHE] Deleting cache:', cacheName);
              return caches.delete(cacheName);
            })
          );
        } catch (error) {
          console.warn('⚠️ [CACHE] Could not clear caches:', error);
        }
      }
      
      // Clear any stored data in sessionStorage related to financial cases
      const keysToRemove = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (key.includes('financial') || key.includes('cache'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => {
        console.log('🗑️ [SESSION] Removing session key:', key);
        sessionStorage.removeItem(key);
      });
      
      // Force reload with no-cache headers
      await loadFinancialCases(true);
      
      // Success toast
      toast.success('✅ Cache đã được xóa và dữ liệu đã được tải lại!', { id: toastId });
      
    } catch (error) {
      console.error('❌ [REFRESH] Error during refresh:', error);
      toast.error('❌ Có lỗi xảy ra khi làm mới dữ liệu', { id: toastId });
    }
  };

  // Load financial cases với phân trang
  const loadFinancialCases = async (forceNoCache = false, page = 1) => {
    setLoading(true);
    try {
      // Tạo params cho API call với phân trang
      const apiParams = {
        page: page,
        limit: pageSize
      };

      // Nếu forceNoCache = true, thêm timestamp để bypass cache
      if (forceNoCache) {
        apiParams._t = Date.now();
        apiParams['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        apiParams['Pragma'] = 'no-cache';
        apiParams['Expires'] = '0';
      }
        
      const response = await financialCaseApi.getAllFinancialCases(token, apiParams);
      console.log('🔍 [DEBUG] Financial cases loaded:', response);
      
      if (forceNoCache) {
        console.log('✅ [REFRESH] Data loaded with cache bypass');
      }
      
      if (response.success) {
        const allCases = response.data || response;
        
        // Cập nhật thông tin phân trang
        if (response.pagination) {
          setCurrentPage(response.pagination.currentPage || page);
          setTotalPages(response.pagination.totalPages || 1);
          setTotalRecords(response.pagination.totalRecords || allCases.length);
        }
        
        // Tính thống kê trên tất cả cases (cần load riêng để có tổng số chính xác)
        await loadStatistics();
        
        // Chỉ hiển thị cases pending trong bảng
        const pendingCases = allCases.filter(case_ => 
          case_.status === 'pending'
        );
        setFinancialCases(pendingCases);
        setFilteredCases(pendingCases);
      } else {
        toast.error('Có lỗi xảy ra khi tải danh sách financial cases');
      }
    } catch (error) {
      console.error('Error loading financial cases:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách financial cases');
    } finally {
      setLoading(false);
    }
  };

  // Load statistics riêng biệt
  const loadStatistics = async () => {
    try {
      const response = await financialCaseApi.getStatistics(token);
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    loadFinancialCases(false, newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset về trang đầu
    loadFinancialCases(false, 1);
  };

  // Calculate statistics
  const calculateStatistics = (cases) => {
    const stats = {
      total: cases.length,
      pending: cases.filter(c => c.status === 'pending').length,
      approved: cases.filter(c => c.status === 'approved').length,
      rejected: cases.filter(c => c.status === 'rejected').length,
      cancelled: cases.filter(c => c.status === 'cancelled').length
    };
    setStatistics(stats);
  };

  // Filter cases
  const filterCases = () => {
    let filtered = financialCases;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(case_ => 
        case_.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        case_.seller_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.status === statusFilter);
    }

    // Currency filter
    if (currencyFilter !== 'all') {
      filtered = filtered.filter(case_ => case_.currency === currencyFilter);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const today = new Date();
      let startDate, endDate;

      switch (dateFilter) {
        case 'today':
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
          break;
        case 'week':
          const dayOfWeek = today.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          startDate = new Date(today);
          startDate.setDate(today.getDate() - daysFromMonday);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        case 'month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today);
          endDate.setHours(23, 59, 59, 999);
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        filtered = filtered.filter(case_ => {
          const caseDate = new Date(case_.date);
          return caseDate >= startDate && caseDate <= endDate;
        });
      }
    }

    setFilteredCases(filtered);
  };

  // Handle approve/reject
  const handleApproveReject = async () => {
    if (!actionNote.trim()) {
      toast.error('Vui lòng nhập lý do');
      return;
    }

    setSubmitting(true);
    try {
      const response = await financialCaseApi.approveRejectCase(
        token, 
        selectedCase.id, 
        actionType, 
        actionNote
      );

      if (response.success) {
        toast.success(response.message);
        setShowActionModal(false);
        setShowDetailModal(false);
        setActionNote('');
        setActionType('');
        loadFinancialCases(); // Reload data
      } else {
        toast.error(response.message || 'Có lỗi xảy ra');
      }
    } catch (error) {
      console.error('Error approving/rejecting case:', error);
      toast.error('Có lỗi xảy ra khi xử lý case');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'cancelled': return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  // Get type color
  const getTypeColor = (type) => {
    return type === 'income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Effects
  useEffect(() => {
    if (token) {
      loadFinancialCases();
    }
  }, [token]);

  useEffect(() => {
    filterCases();
  }, [searchTerm, statusFilter, dateFilter, currencyFilter, financialCases]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-green-400/20 to-blue-600/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

             <div className="relative z-10 p-4 sm:p-6 lg:p-8 w-full">
         <div className="w-full">
          {/* Header */}
          <div className="mb-8 animate-fade-in">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                      Quản lý Financial Cases
                    </h1>
                    <p className="text-gray-600 text-base sm:text-lg mt-1">
                      Dashboard chuyên nghiệp để xem và xử lý tất cả financial cases từ sellers
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href="/accountant/statistics"
                  className="group flex items-center justify-center space-x-2 bg-white/80 backdrop-blur-sm px-6 py-3 rounded-2xl shadow-lg border border-white/20 hover:shadow-xl hover:bg-white transition-all duration-300 transform hover:scale-105"
                >
                  <BarChart3 className="w-5 h-5 text-blue-600 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="text-gray-700 font-semibold">Xem Thống kê</span>
                </a>
                <button
                  onClick={refreshWithClearCache}
                  className="group flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 disabled:opacity-50"
                  disabled={loading}
                  title="Làm mới và xóa cache"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-300`} />
                  <span className="font-semibold">Làm mới</span>
                </button>
              </div>
            </div>
          </div>

                     {/* Statistics Cards */}
           <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
            {[
              {
                title: 'Tổng Cases',
                value: statistics.total,
                icon: FileText,
                color: 'from-blue-500 to-blue-600',
                bgColor: 'bg-blue-100',
                textColor: 'text-blue-600',
                trend: '+12%',
                trendText: 'so với tháng trước',
                trendIcon: TrendingUpIcon
              },
              {
                title: 'Chờ Duyệt',
                value: statistics.pending,
                icon: Clock,
                color: 'from-yellow-500 to-orange-500',
                bgColor: 'bg-yellow-100',
                textColor: 'text-yellow-600',
                trend: 'Cần xử lý',
                trendText: '',
                trendIcon: Zap
              },
              {
                title: 'Đã Duyệt',
                value: statistics.approved,
                icon: CheckCircle,
                color: 'from-green-500 to-emerald-600',
                bgColor: 'bg-green-100',
                textColor: 'text-green-600',
                trend: '+8%',
                trendText: 'so với tuần trước',
                trendIcon: TrendingUpIcon
              },
              {
                title: 'Từ Chối',
                value: statistics.rejected,
                icon: XCircle,
                color: 'from-red-500 to-pink-600',
                bgColor: 'bg-red-100',
                textColor: 'text-red-600',
                trend: '-3%',
                trendText: 'so với tháng trước',
                trendIcon: TrendingDownIcon
              }
            ].map((card, index) => (
              <div
                key={card.title}
                className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 hover:shadow-2xl hover:bg-white transition-all duration-500 transform hover:scale-105 animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-600">{card.title}</p>
                    <p className={`text-3xl font-bold ${card.textColor}`}>{card.value}</p>
                    <div className="flex items-center gap-1">
                      <card.trendIcon className={`w-4 h-4 ${card.textColor}`} />
                      <span className={`text-sm font-medium ${card.textColor}`}>
                        {card.trend} {card.trendText}
                      </span>
                    </div>
                  </div>
                  <div className={`p-4 bg-gradient-to-br ${card.color} rounded-2xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <card.icon className="w-8 h-8 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 mb-8 p-6 animate-fade-in-up" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                  <FilterIcon className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Bộ lọc & Tìm kiếm</h3>
              </div>
            </div>
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-4">
              <div className="relative group">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-blue-500 transition-colors duration-300" />
                <input
                  type="text"
                  placeholder="Tìm kiếm cases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
              >
                <option value="all">Tất cả trạng thái</option>
                <option value="pending">Chờ duyệt</option>
                <option value="approved">Đã duyệt</option>
                <option value="rejected">Từ chối</option>
                <option value="cancelled">Đã hủy</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
              >
                <option value="all">Tất cả thời gian</option>
                <option value="today">Hôm nay</option>
                <option value="week">Tuần này</option>
                <option value="month">Tháng này</option>
              </select>

              <select
                value={currencyFilter}
                onChange={(e) => setCurrencyFilter(e.target.value)}
                className="px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/50 backdrop-blur-sm"
              >
                <option value="all">Tất cả tiền tệ</option>
                <option value="VND">VND</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          {/* Financial Cases Table */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-lg border border-white/20 overflow-hidden animate-fade-in-up" style={{ animationDelay: '500ms' }}>
            <div className="px-6 py-6 border-b border-gray-200/50 bg-gradient-to-r from-gray-50/50 to-blue-50/50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <Target className="w-6 h-6 text-blue-600" />
                    Danh sách Financial Cases
                  </h3>
                  <p className="text-gray-600 mt-1">
                    Hiển thị {filteredCases.length} cases trong tổng số {financialCases.length}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Thu</span>
                    <div className="w-3 h-3 bg-red-500 rounded-full ml-2"></div>
                    <span>Chi</span>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="p-16 text-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
                  <RefreshCw className="w-10 h-10 animate-spin text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Đang tải dữ liệu...</h3>
                <p className="text-gray-500">Vui lòng chờ trong giây lát</p>
              </div>
            ) : filteredCases.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <FileText className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Không có financial cases nào</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Không tìm thấy cases nào phù hợp với bộ lọc hiện tại. Hãy thử thay đổi bộ lọc hoặc tạo case mới.
                </p>
                <button
                  onClick={refreshWithClearCache}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  title="Làm mới và xóa cache"
                >
                  Làm mới
                </button>
              </div>
            ) : (
                             <div className="overflow-x-auto w-full">
                 <table className="w-full min-w-full">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Thông tin Case
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Loại & Số tiền
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Accountant được chỉ định
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Trạng thái
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">
                        Thao tác
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/50 divide-y divide-gray-200/50">
                    {filteredCases.map((case_, index) => (
                      <tr 
                        key={case_.id} 
                        className="hover:bg-blue-50/50 transition-all duration-300 group animate-fade-in-up"
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <td className="px-6 py-6">
                          <div>
                            <div className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                              {case_.title}
                            </div>
                            <div className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {case_.description}
                            </div>
                            <div className="flex items-center mt-3 text-xs text-gray-500">
                              <User className="w-3 h-3 mr-1" />
                              <span className="font-medium">{case_.seller_name}</span>
                              <span className="mx-2">•</span>
                              <span>{formatDate(case_.created_at)}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex items-center gap-4">
                            <span className={`inline-flex items-center px-3 py-2 text-xs font-bold rounded-full ${getTypeColor(case_.type)} transition-all duration-300 group-hover:scale-105`}>
                              {(case_.type === 'income' || case_.type === 'thu') ? (
                                <>
                                  <TrendingUpIcon className="w-3 h-3 mr-1" />
                                  Thu
                                </>
                              ) : (
                                <>
                                  <TrendingDownIcon className="w-3 h-3 mr-1" />
                                  Chi
                                </>
                              )}
                            </span>
                            <div className="text-right">
                              <span className="text-sm font-bold text-gray-900">
                                {formatCurrency(case_.amount, case_.currency)}
                              </span>
                              <div className="text-xs text-gray-500">
                                {case_.currency}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-6">
                          {case_.assigned_accountant_name ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                <span className="text-blue-600 font-semibold text-sm">
                                  {case_.assigned_accountant_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{case_.assigned_accountant_name}</div>
                                <div className="text-xs text-gray-500">{case_.assigned_accountant_email}</div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Chưa chỉ định</span>
                          )}
                        </td>
                        <td className="px-6 py-6">
                          <span className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-bold border-2 ${getStatusColor(case_.status)} transition-all duration-300 group-hover:scale-105`}>
                            {getStatusIcon(case_.status)}
                            <span className="ml-1">
                              {case_.status === 'pending' && 'Chờ duyệt'}
                              {case_.status === 'approved' && 'Đã duyệt'}
                              {case_.status === 'rejected' && 'Từ chối'}
                              {case_.status === 'cancelled' && 'Đã hủy'}
                            </span>
                          </span>
                        </td>
                        <td className="px-6 py-6">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedCase(case_);
                                setShowDetailModal(true);
                              }}
                              className="p-3 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
                              title="Xem chi tiết"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {case_.status === 'pending' && canApproveRejectCase(case_) && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedCase(case_);
                                    setActionType('approve');
                                    setShowActionModal(true);
                                  }}
                                  className="p-3 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
                                  title="Duyệt"
                                >
                                  <CheckSquare className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedCase(case_);
                                    setActionType('reject');
                                    setShowActionModal(true);
                                  }}
                                  className="p-3 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-2xl transition-all duration-300 transform hover:scale-110 shadow-sm hover:shadow-md"
                                  title="Từ chối"
                                >
                                  <XSquare className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            
                            {/* Hiển thị thông báo nếu không có quyền duyệt */}
                            {case_.status === 'pending' && !canApproveRejectCase(case_) && case_.assigned_accountant_id && (
                              <div className="flex items-center justify-center">
                                <span className="text-xs text-gray-500 italic px-2 py-1 bg-gray-50 rounded-full border">
                                  🔒 Chỉ {case_.assigned_accountant_name || 'accountant được chỉ định'} mới có thể duyệt
                                </span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedCase && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-in">
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-blue-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                    <Award className="w-6 h-6 text-blue-600" />
                    Chi tiết Financial Case
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    ID: #{selectedCase.id}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-2xl transition-all duration-300 transform hover:scale-110"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Main Info Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-6 border border-blue-100">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-gray-900">Thông tin chính</h4>
                  <span className={`inline-flex items-center px-4 py-2 text-sm font-bold rounded-full ${getTypeColor(selectedCase.type)} transition-all duration-300 hover:scale-105`}>
                    {(selectedCase.type === 'income' || selectedCase.type === 'thu') ? (
                      <>
                        <TrendingUpIcon className="w-4 h-4 mr-2" />
                        Thu nhập
                      </>
                    ) : (
                      <>
                        <TrendingDownIcon className="w-4 h-4 mr-2" />
                        Chi phí
                      </>
                    )}
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="font-bold text-gray-900 mb-3 text-lg">{selectedCase.title}</h5>
                    <p className="text-gray-600 text-sm leading-relaxed">{selectedCase.content}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {formatCurrency(selectedCase.amount, selectedCase.currency)}
                      <span className="text-sm text-gray-500 pl-2">
                      {selectedCase.currency}
                    </span>
                    </div>
                    
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Thông tin Seller
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tên Seller</label>
                      <p className="text-sm font-bold text-gray-900">{selectedCase.seller_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-sm text-gray-900">{selectedCase.seller_email || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    Thông tin thời gian
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Ngày tạo</label>
                      <p className="text-sm font-bold text-gray-900">{formatDate(selectedCase.created_at)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Trạng thái</label>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-bold border-2 ${getStatusColor(selectedCase.status)}`}>
                          {getStatusIcon(selectedCase.status)}
                          <span className="ml-2">
                            {selectedCase.status === 'pending' && 'Chờ duyệt'}
                            {selectedCase.status === 'approved' && 'Đã duyệt'}
                            {selectedCase.status === 'rejected' && 'Từ chối'}
                            {selectedCase.status === 'cancelled' && 'Đã hủy'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Thông tin người duyệt */}
                {(selectedCase.status === 'approved' || selectedCase.status === 'rejected') && selectedCase.accountant_name && (
                  <div className="bg-blue-50 rounded-3xl p-6 border border-blue-100">
                    <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <CheckSquare className="w-5 h-5 text-blue-600" />
                      Thông tin người duyệt
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tên Accountant</label>
                        <p className="text-sm font-bold text-gray-900">{selectedCase.accountant_name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Email</label>
                        <p className="text-sm text-gray-900">{selectedCase.accountant_email || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Hành động</label>
                        <p className="text-sm font-bold text-gray-900">
                          {selectedCase.status === 'approved' ? 'Đã duyệt' : 'Đã từ chối'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes Section */}
              {selectedCase.note && (
                <div className="bg-yellow-50 rounded-3xl p-6 border border-yellow-100">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-yellow-600" />
                    Ghi chú
                  </h4>
                  <p className="text-gray-700 text-sm leading-relaxed">{selectedCase.note}</p>
                </div>
              )}

              {selectedCase.status === 'pending' && canApproveRejectCase(selectedCase) && (
                <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setActionType('approve');
                      setShowActionModal(true);
                    }}
                    className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-2xl hover:from-green-700 hover:to-emerald-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <CheckSquare className="w-4 h-4" />
                    <span className="font-semibold">Duyệt</span>
                  </button>
                  <button
                    onClick={() => {
                      setActionType('reject');
                      setShowActionModal(true);
                    }}
                    className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-3 rounded-2xl hover:from-red-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <XSquare className="w-4 h-4" />
                    <span className="font-semibold">Từ chối</span>
                  </button>
                </div>
              )}

              {selectedCase.status === 'pending' && !canApproveRejectCase(selectedCase) && selectedCase.assigned_accountant_id && (
                <div className="flex items-center justify-center pt-6 border-t border-gray-200">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 text-center">
                    <div className="flex items-center justify-center gap-2 text-yellow-800">
                      <Lock className="w-5 h-5" />
                      <span className="font-semibold">
                        Chỉ {selectedCase.assigned_accountant_name || 'accountant được chỉ định'} mới có thể duyệt case này
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showActionModal && selectedCase && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl animate-scale-in">
            <div className={`p-6 border-b border-gray-200 ${
              actionType === 'approve' 
                ? 'bg-gradient-to-r from-green-50 to-emerald-50' 
                : 'bg-gradient-to-r from-red-50 to-pink-50'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-2xl ${
                    actionType === 'approve' 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-red-100 text-red-600'
                  }`}>
                    {actionType === 'approve' ? (
                      <CheckSquare className="w-8 h-8" />
                    ) : (
                      <XSquare className="w-8 h-8" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {actionType === 'approve' ? 'Duyệt' : 'Từ chối'} Financial Case
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Case: {selectedCase.title}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setActionNote('');
                    setActionType('');
                  }}
                  className="p-3 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-2xl transition-all duration-300 transform hover:scale-110"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <label className="block text-sm font-bold text-gray-700 mb-3">
                  Lý do {actionType === 'approve' ? 'duyệt' : 'từ chối'}
                </label>
                <textarea
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/20 focus:border-blue-500 resize-none transition-all duration-300"
                  placeholder={`Nhập lý do ${actionType === 'approve' ? 'duyệt' : 'từ chối'}...`}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Lý do này sẽ được ghi lại trong lịch sử của case
                </p>
              </div>

              <div className="flex items-center justify-end gap-4">
                <button
                  onClick={() => {
                    setShowActionModal(false);
                    setActionNote('');
                    setActionType('');
                  }}
                  className="px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-2xl transition-all duration-300 font-semibold"
                  disabled={submitting}
                >
                  Hủy
                </button>
                <button
                  onClick={handleApproveReject}
                  disabled={submitting || !actionNote.trim()}
                  className={`flex items-center gap-2 px-6 py-3 rounded-2xl transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 ${
                    actionType === 'approve'
                      ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700 disabled:from-green-400 disabled:to-emerald-400'
                      : 'bg-gradient-to-r from-red-600 to-pink-600 text-white hover:from-red-700 hover:to-pink-700 disabled:from-red-400 disabled:to-pink-400'
                  }`}
                >
                  {submitting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : actionType === 'approve' ? (
                    <CheckSquare className="w-4 h-4" />
                  ) : (
                    <XSquare className="w-4 h-4" />
                  )}
                  <span>
                    {submitting ? 'Đang xử lý...' : actionType === 'approve' ? 'Duyệt' : 'Từ chối'}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes fade-in-up {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        @keyframes slide-in-right {
          from { 
            opacity: 0; 
            transform: translateX(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.4s ease-out;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #6366f1);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #4f46e5);
        }
      `}</style>
    </div>
  );
};

export default AccountantFinance; 